import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, FileText, Code, CheckCircle, Rocket } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Consultation',
    description: 'We start with a deep dive into your business goals, target audience, and vision.',
    icon: MessageSquare,
  },
  {
    number: '02',
    title: 'Proposal',
    description: 'Receive a detailed proposal with timeline, deliverables, and transparent pricing.',
    icon: FileText,
  },
  {
    number: '03',
    title: 'Design & Build',
    description: 'Our team crafts your vision into reality with iterative feedback loops.',
    icon: Code,
  },
  {
    number: '04',
    title: 'QA & SEO',
    description: 'Rigorous testing across devices and browsers, plus SEO optimization.',
    icon: CheckCircle,
  },
  {
    number: '05',
    title: 'Launch & Support',
    description: 'Go live with confidence. We provide ongoing support and maintenance.',
    icon: Rocket,
  },
];

const Process = () => {
  return (
    <section 
      id="process" 
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #0a0a12 50%, #050508 100%)',
      }}
      data-testid="process-section"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-[#7B2EFF]/5 rounded-full blur-[180px] translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-4">
            Our Process
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-white">From Idea to Launch</h2>
          <p className="mt-4 text-white/50 max-w-2xl mx-auto text-lg">
            A streamlined, transparent process designed to deliver results efficiently.
          </p>
        </motion.div>

        {/* Timeline - Glass Cards */}
        <div className="relative max-w-3xl mx-auto">
          {/* Timeline Line - Glowing */}
          <div className="absolute left-8 md:left-10 top-0 bottom-0 w-px">
            <div className="h-full w-full bg-gradient-to-b from-[#7B2EFF] via-[#7B2EFF]/30 to-transparent" />
            <div className="absolute inset-0 w-full bg-gradient-to-b from-[#7B2EFF] via-[#7B2EFF]/30 to-transparent blur-sm" />
          </div>

          {/* Steps */}
          <div className="space-y-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative flex gap-6 md:gap-8 group"
                data-testid={`process-step-${step.number}`}
              >
                {/* Timeline Node - Glowing Glass */}
                <motion.div 
                  className="relative z-10 flex-shrink-0"
                  whileHover={{ scale: 1.1 }}
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl glass-card grid place-items-center group-hover:shadow-[0_0_30px_rgba(123,46,255,0.4)] transition-all">
                    <step.icon className="w-7 h-7 md:w-8 md:h-8 text-[#7B2EFF]" />
                  </div>
                  {/* Glow dot */}
                  <div className="absolute top-1/2 left-0 -translate-x-[calc(100%+1rem)] -translate-y-1/2 w-3 h-3 rounded-full bg-[#7B2EFF] shadow-[0_0_15px_rgba(123,46,255,0.8)]" />
                </motion.div>

                {/* Content Card - Glass Panel */}
                <div className="flex-1 glass-card rounded-2xl p-6 group-hover:border-[#7B2EFF]/40 transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 rounded-full text-[#7B2EFF] font-mono text-sm font-semibold bg-[#7B2EFF]/10 border border-[#7B2EFF]/20">
                      Step {step.number}
                    </span>
                    <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                  </div>
                  <p className="text-white/50 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
